import type { PickColorEvent } from '@blocksuite/affine-components/color-picker';
import {
  DefaultTheme,
  LineWidth,
  resolveColor,
  StrokeStyle,
} from '@blocksuite/affine-model';
import {
  type ToolbarModuleConfig,
  ToolbarModuleExtension,
} from '@blocksuite/affine-shared/services';
import { BlockFlavourIdentifier } from '@blocksuite/std';
import { html } from 'lit';

import { BrushHeadingBlockModel } from '../schemas/brush-heading-model.js';
import { createCanvasTextStyleActions } from '../text-style-toolbar.js';
import { resolveBrushHeadingColors } from './brush-heading-visual.js';

const textStyleActions = createCanvasTextStyleActions({
  getModels: ctx => ctx.getSurfaceModelsByType(BrushHeadingBlockModel),
  fallbackColor: (model, ctx) =>
    resolveBrushHeadingColors(model.props.color, '', ctx.theme.edgeless$.value)
      .foreground,
});

const brushHeadingToolbarConfig = {
  actions: [
    {
      id: 'a.brush-color',
      content(ctx) {
        const models = ctx.getSurfaceModelsByType(BrushHeadingBlockModel);
        if (!models.length) return null;

        const theme = ctx.theme.edgeless$.value;
        const originalFillColor = models[0].props.color;
        const fillColor = resolveColor(originalFillColor, theme);
        const onPickFillColor = (event: CustomEvent<PickColorEvent>) => {
          event.stopPropagation();
          switch (event.detail.type) {
            case 'start':
              ctx.store.captureSync();
              break;
            case 'pick': {
              const value = event.detail.detail.value;
              const color = typeof value === 'object' ? { ...value } : value;
              models.forEach(model => ctx.store.updateBlock(model, { color }));
              break;
            }
            case 'end':
              break;
          }
        };

        return html`<edgeless-shape-color-picker
          .fillOnly=${true}
          @pickFillColor=${onPickFillColor}
          .palettes=${DefaultTheme.Palettes}
          .payload=${{
            fillColor,
            strokeColor: 'transparent',
            strokeWidth: LineWidth.Two,
            strokeStyle: StrokeStyle.Solid,
            originalFillColor,
            originalStrokeColor: 'transparent',
            theme,
            enableCustomColor: ctx.features.getFlag('enable_color_picker'),
          }}
        ></edgeless-shape-color-picker>`;
      },
    },
    ...textStyleActions,
  ],
  when: ctx => ctx.getSurfaceModelsByType(BrushHeadingBlockModel).length > 0,
} as const satisfies ToolbarModuleConfig;

export const brushHeadingToolbarExtension = ToolbarModuleExtension({
  id: BlockFlavourIdentifier('affine:surface:brush-heading'),
  config: brushHeadingToolbarConfig,
});
